import { ExpandMore as ExpandMoreIcon, LockIcon } from 'components/icons';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Badge } from 'components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { ReactNode, useId, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { InvoiceData, InvoiceMatterDetailsDto, InvoiceRowForm, InvoiceTotals, InvoiceTotalsForm } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import { CaseMarks, CaseMeta, CaseNamesEdit, CaseOtherNames } from '../details-tab/invoice-matters/InvoiceCaseCard';
import { InvoiceMatterNameDetails } from '../details-tab/invoice-matters/InvoiceMatterNameDetails';
import { SECTION_CARD, SECTION_HEADER, SECTION_PANEL, SECTION_TITLE } from '../details-tab/SectionCard';
import InvoiceExpenseTotals from './InvoiceExpenseTotals';
import InvoiceRowTotals from './InvoiceRowTotals';
import InvoiceTimecardTotals from './InvoiceTimecardTotals';
import AdminFeeTotals from './admin-fee/AdminFeeTotals';

interface Props {
    invoiceTotals?: InvoiceTotals;
    invoice: InvoiceData;
    classifiers: Record<string, ClassifierData>;
    isInViewMode: boolean;
    handleAddRow: (data: InvoiceRowForm) => void;
    handleAddAdminFee: (data: InvoiceRowForm) => void;
    /** Draws this case's card alone. */
    matterId?: number;
}

/**
 * One case's totals. A click anywhere on the title row toggles it, except on the case name, which stops the
 * click so its text can be selected; the chevron is the keyboard's way in, and it stops its own click so it
 * does not toggle twice.
 */
const CaseTotalsCard = ({
    defaultOpen,
    fullName,
    title,
    children,
}: {
    defaultOpen: boolean;
    fullName: string;
    title: ReactNode;
    children: ReactNode;
}) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Collapsible open={open} onOpenChange={setOpen} className={SECTION_CARD}>
            <div className={cn(SECTION_HEADER, 'cursor-default')} onClick={() => setOpen(!open)}>
                <div className="my-2.5 ml-2 flex grow">{title}</div>
                <CollapsibleTrigger
                    aria-label={t('invoices.details.case', { fullName })}
                    onClick={(event) => event.stopPropagation()}
                    className="group focus-visible:ring-ring/50 flex cursor-pointer rounded-sm outline-none focus-visible:ring-3"
                >
                    <ExpandMoreIcon className="text-grey-500 transition-transform group-data-[panel-open]:rotate-90" />
                </CollapsibleTrigger>
            </div>
            <CollapsibleContent keepMounted className={SECTION_PANEL}>
                <div className="border-divider border-t p-[15px]">{children}</div>
            </CollapsibleContent>
        </Collapsible>
    );
};

/**
 * The new design's Case totals: one card per case with its blocks under a heading titled with its case number, so it
 * reads as that case's even scrolled into view alone. A prepayment invoice has no case card, so the case's name sits
 * beside the title, its marks, people and other names under it, and its names edit in place there; so do a split
 * copy's, whose case card only reads. A credit note's says it only reads.
 */
const CaseTotalsSection = ({
    invoice,
    invoiceMatter,
    classifiers,
    matterNumber,
    isReadOnly,
    children,
}: {
    invoice: InvoiceData;
    invoiceMatter?: InvoiceMatterDetailsDto;
    classifiers: Record<string, ClassifierData>;
    matterNumber: string;
    isReadOnly: boolean;
    children: ReactNode;
}) => {
    const headingId = useId();
    const { isCredit, isPrepayment } = useInvoice(invoice);
    const editsNames = isPrepayment || (invoice.isSplit ?? false);
    const card = (edit: ReactNode) => (
        <section
            aria-labelledby={headingId}
            className="bg-card shadow-card text-foreground @container overflow-hidden rounded-[10px] pb-2 outline-hidden"
        >
            <div className="flex min-h-14 items-center gap-2 px-5 py-2.5">
                <h2 id={headingId} className="text-section m-0 min-w-0 flex-auto">
                    <FormattedMessage id="invoices.case-totals.title" values={{ case: matterNumber }} />
                    {isPrepayment && invoiceMatter ? (
                        <span className="text-meta text-foreground-tertiary ms-2 font-normal">
                            {invoiceMatter.invoiceMatterName || invoiceMatter.matterName}
                        </span>
                    ) : null}
                </h2>
                {isCredit && !!invoice.debitInvoice ? (
                    <Badge className="bg-neutral-tint text-foreground-secondary">
                        <LockIcon aria-hidden data-icon="inline-start" />
                        <FormattedMessage id="invoices.case-card.read-only-credit" />
                    </Badge>
                ) : null}
                {edit}
            </div>
            {isPrepayment && invoiceMatter ? (
                <div className="flex flex-col gap-2 px-5 pb-3">
                    <div className="flex flex-wrap items-center gap-2">
                        <CaseMarks invoice={invoice} invoiceMatter={invoiceMatter} classifiers={classifiers} />
                    </div>
                    <CaseMeta invoiceMatter={invoiceMatter} />
                    <CaseOtherNames invoiceMatter={invoiceMatter} />
                </div>
            ) : null}
            {children}
        </section>
    );
    return editsNames && invoiceMatter ? (
        <CaseNamesEdit invoice={invoice} invoiceMatter={invoiceMatter} canEdit={!isReadOnly}>
            {card}
        </CaseNamesEdit>
    ) : (
        card(null)
    );
};

const getInvoiceDetailsTotalsItem = (index: number, invoiceTotals?: InvoiceTotals) => {
    return invoiceTotals?.invoiceDetailsTotals && invoiceTotals?.invoiceDetailsTotals.length > 0
        ? invoiceTotals?.invoiceDetailsTotals[index]
        : undefined;
};

const InvoiceMatterTotals = ({
    invoiceTotals,
    invoice,
    classifiers,
    isInViewMode,
    handleAddRow,
    handleAddAdminFee,
    matterId,
}: Props) => {
    const isLocalizedFieldsEnabled = useIsFeatureEnabled([
        FeatureFlag.LOCALIZED_FIELDS,
        FeatureFlag.LOCALIZED_FIELDS_INVOICES,
    ]);
    const isAdminFeeFeatureEnabled = useIsFeatureEnabled(FeatureFlag.ADMIN_FEE_MODAL_INVOICES);
    const { control } = useFormContext<InvoiceTotalsForm>();
    const { fields } = useFieldArray({
        control,
        name: 'invoiceDetailsTotals',
        keyName: 'formKey',
    });
    const paymentCurrency = invoiceTotals?.paymentCurrency.currencyTypeCode ?? '';
    const isMultiReviewApprovalEnabled = useIsFeatureEnabled(FeatureFlag.MULTI_REVIEW_APPROVE_JOINT_INVOICE);
    const isUserPrivilegedToUpdateApproved = useHasPermissions(['UPDATE.INVOICES.STATE_APPROVED']);
    const { isPaymentCurrencyMode } = usePaymentCurrencyMode(invoice);
    const { isCredit, isPrepayment } = useInvoice(invoice);
    const isBlankCreditNote = isCredit && !invoice.debitInvoice;
    const isNewDesign = useIsNewDesign();

    const accordionContent = useMemo(
        () =>
            fields.map((field, index) => {
                if (matterId !== undefined && field.matterId !== matterId) {
                    return null;
                }
                const invoiceMatter = invoice.invoiceMatters.find((item) => item.matterId === field.matterId);
                const invoiceMatterTotals = getInvoiceDetailsTotalsItem(index, invoiceTotals);
                const isInvoiceMatterReadOnly =
                    isInViewMode ||
                    (isMultiReviewApprovalEnabled && !!invoiceMatter?.approvedAt && !isUserPrivilegedToUpdateApproved);

                const blocks = (
                    <>
                        {!isBlankCreditNote && !isPrepayment && (
                            <InvoiceTimecardTotals
                                invoiceTotals={invoiceMatterTotals}
                                isInViewMode={isInvoiceMatterReadOnly}
                                invoice={invoice}
                                index={index}
                                classifiers={classifiers}
                                matterId={field.matterId}
                                paymentCurrency={paymentCurrency}
                            />
                        )}
                        <InvoiceRowTotals
                            invoiceTotals={invoiceMatterTotals}
                            classifiers={classifiers}
                            isInViewMode={isInvoiceMatterReadOnly}
                            invoice={invoice}
                            index={index}
                            matterId={field.matterId}
                            handleAddRow={handleAddRow}
                            paymentCurrency={paymentCurrency}
                        />
                        {!isBlankCreditNote && !isPrepayment && isAdminFeeFeatureEnabled ? (
                            <AdminFeeTotals
                                invoiceTotals={invoiceMatterTotals}
                                isInViewMode={isInvoiceMatterReadOnly}
                                invoice={invoice}
                                matterId={field.matterId}
                                handleAddRow={handleAddAdminFee}
                                paymentCurrency={paymentCurrency}
                                baseAmount={
                                    isPaymentCurrencyMode
                                        ? (invoiceMatter?.adminFeeBaseAmountInPaymentCurrency ?? 0)
                                        : (invoiceMatter?.adminFeeBaseAmount ?? 0)
                                }
                                defaultAdminFeeRate={
                                    invoiceMatter?.hasAdminFeeAllowed ? invoiceMatter?.adminFeePercentage : undefined
                                }
                                classifiers={classifiers}
                            />
                        ) : null}
                        {!isBlankCreditNote && !isPrepayment && (
                            <InvoiceExpenseTotals
                                invoiceTotals={invoiceMatterTotals}
                                isInViewMode={isInvoiceMatterReadOnly}
                                invoice={invoice}
                                index={index}
                                matterId={field.matterId}
                                paymentCurrency={paymentCurrency}
                            />
                        )}
                    </>
                );
                return isNewDesign ? (
                    <CaseTotalsSection
                        key={field.matterId}
                        invoice={invoice}
                        invoiceMatter={invoiceMatter}
                        classifiers={classifiers}
                        matterNumber={field.matterNumber}
                        isReadOnly={isInvoiceMatterReadOnly}
                    >
                        {blocks}
                    </CaseTotalsSection>
                ) : (
                    <CaseTotalsCard
                        key={field.matterId}
                        defaultOpen={index === 0}
                        fullName={invoiceMatter?.fullName ?? field.fullName ?? ''}
                        title={
                            invoiceMatter ? (
                                <InvoiceMatterNameDetails
                                    invoice={invoice}
                                    invoiceMatter={invoiceMatter}
                                    isInViewMode={isInvoiceMatterReadOnly}
                                    isLocalizedFieldsEnabled={isLocalizedFieldsEnabled}
                                    classifiers={classifiers}
                                />
                            ) : (
                                <p className={SECTION_TITLE}>
                                    <FormattedMessage
                                        id="invoices.details.case"
                                        values={{ fullName: field.fullName ?? '' }}
                                    />
                                </p>
                            )
                        }
                    >
                        {blocks}
                    </CaseTotalsCard>
                );
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            fields,
            invoice,
            isInViewMode,
            isLocalizedFieldsEnabled,
            invoiceTotals,
            classifiers,
            handleAddRow,
            handleAddAdminFee,
            isAdminFeeFeatureEnabled,
            isNewDesign,
            matterId,
        ]
    );

    return <div className="new:gap-(--card-gap) flex flex-col gap-6">{accordionContent}</div>;
};

export default InvoiceMatterTotals;
